import { LoadingStatuses } from '@/constants/constants';
import { AssetType, InventoryFileEntity, OrderJobTask, TitleEntity, UserProfile } from './slice_types';

export type Milliseconds = number;
export type Frames = number;
export type FrameIndex = number;
export type Pixels = number;

// Frame rate examples:
// [24, 1] — 24 fps
// [30000, 1001] — 29.97 fps
export type FrameRate = [number, number]; // [numerator, denominator]

export type InventoryFileWithProxy = InventoryFileEntity & { proxyId?: number };

export enum TemplateType {
  Generic = 'generic',
  Custom = 'custom'
}

export interface AssemblyTemplate {
  id: number;
  name: string;
  slug: string;
  template_type: TemplateType;
  data: {
    segments: SegmentItem[];
    overlays: OverlayLayer[];
  };
  description: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by: UserProfile | null;
  updated_by: UserProfile | null;
  deleted_by: UserProfile | null;
}

export interface AssemblyTemplateBEResponse extends Omit<AssemblyTemplate, 'data'> {
  data: {
    segments: SegmentItemBEResponse[];
    overlays: OverlayLayerBEResponse[];
  };
}

export interface AssemblyTemplateBERequest extends Omit<AssemblyTemplate, 'data'> {
  data: {
    segments: SegmentItemBERequest[];
    overlays: OverlayLayerBERequest[];
  };
}

type ContentAssemblyJob = OrderJobTask['job'] & {
  order: {
    // there are more fields coming from the server, but so far these are only fields actually used
    hid: string;
  };
};

type AssemblyInputsDataResponse = (
  | {
      type: 'segments';
      cuts: SegmentItemBEResponse[];
    }
  | {
      type: 'overlays';
      cuts: OverlayItemBEResponse[];
    }
)[];

export type AssemblyInputsDataRequest = (
  | {
      type: 'segments';
      cuts: SegmentItemBERequest[];
    }
  | {
      type: 'overlays';
      cuts: OverlayItemBERequest[];
    }
)[];

export interface AssemblyInputsResponse extends Omit<AssemblyTemplate, 'data'> {
  data: AssemblyInputsDataResponse;
  status: string;
}

export interface ContentAssembly extends OrderJobTask {
  job: ContentAssemblyJob;
  assembly_template_inputs: AssemblyInputsResponse;
  version: TitleEntity;
}

export interface PackagingState {
  activeTemplate: AssemblyTemplate | null;
  /**
   * `activeCutType` represents a timeline entity (segments or overlay) that is in the process of being assigned with
   * some media (for example, video or image).
   */
  activeCutType: TimelineItem | null;
  activeHover: TimelineItem[] | null;
  toolboxOptions: OptionItem[];
  loadingStatus: LoadingStatuses | null; // `null` means loading is not started yet
  timelineScale: number;
  timelineFrameRate: FrameRate;
  movingOverlayData: MovingOverlayData | null; // data for the overlay that is currently being dragged
  /**
   * This property indicates that timeline right now is in the process of being edited. For example: user started to
   * drag a segment/overlay and not yet dropped it anywhere; user started to resize overlay and didn't release the
   * resize handle.
   * At the moment of creation this property is used to prevent undo/redo edits while in the middle of editing operation.
   */
  isEditingTimeline: boolean;
  /**
   * The index of the frame that is currently displayed in the player. This value is used to track the playback, not
   * to change playback position.
   */
  playerCurrentFrame: number;
  /**
   * `isPlayerOpened` specifies whether preview panel (containing the video player) is expanded or collapsed
   */
  isPlayerOpened: boolean;
  isPlaying: boolean;
  isToolboxDrawerOpen: boolean;

  inventoryAssignmentModal: {
    selectedFile: InventoryFileWithProxy | null;
    previewedFile: InventoryFileWithProxy | null;
  };

  contentAssembly: null | ContentAssembly;
}

export interface PackagingTimelineState {
  segments: SegmentItem[];
  overlays: OverlayLayer[];
}

/**
 * `MovingOverlayData` represents data for an overlay that is being dragged. Object is used to:
 * - Display such called ghost, the silhouette of the overlay on the place where it's going to land when released
 * (dropped).
 * - Getting new position for the overlay when it's dropped on the timeline.
 */
export interface MovingOverlayData {
  originalLayerIndex: number | null; // originalLayerIndex can be `null` when drags new option to the overlays tray
  targetLayerIndex: number;
  duration: Frames;
  originalStart: FrameIndex | null; // originalStart can be `null` in case when user drags new option to the overlays tray
  originalEnd: FrameIndex | null; // originalEnd can be `null` in case when user drags new option to the overlays tray
  newStart: FrameIndex | null; // new start frame can be `null` in case if overlay can not be dropped in the specified location
  newEnd: FrameIndex | null; // new start frame can be `null` in case if overlay can not be dropped in the specified location
}

export enum DRAGGABLE_TYPES {
  SEGMENT = 'segment',
  OVERLAY = 'overlay',
  OPTION = 'option'
}

export enum DROPPABLE_TYPES {
  OVERLAYS_TRAY = 'overlays-tray',
  SEGMENTS_TRAY = 'segments-tray',
  SEGMENT = 'segment' // segments also can be droppable, since we can re-order them
}

export enum OVERLAY_KIND {
  RATING_CARD = 'rating-card',
  HASHTAG = 'hashtag',
  LOWER_THIRD = 'lower-third',
  BUG = 'bug'
}

export enum SEGMENT_KIND {
  PROMO = 'promo',
  OPEN_TEASE = 'open',
  BODY = 'body',
  BODY_WITH_CREDITS = 'body-with-credits',
  BRAND_ID = 'brand-id',
  UNDERWRITING = 'underwriting',
  CREDITS = 'credits',
  CREDITS_WITH_WEBTAG_AND_OFFER = 'credits-with-webtag-and-offer',
  CREDITS_WITH_WEBTAG = 'credits-with-webtag',
  CREDITS_WITH_OFFER = 'credits-with-offer',
  WEBTAG = 'webtag',
  OFFER = 'offer',
  SYSTEM_CUE = 'system-cue',
  CONTENT_FILLER = 'content-filler',
  FILLER = 'filler',
  PLEDGE_BREAK = 'pledge-break',
  BLACK = 'black',
  ADVISORY = 'advisory',
  NEXT_TIME_TEASE = 'next-time-tease',
  NEXT_TIME_TEASE_WITH_CREDITS = 'next-time-tease-with-credits'
}

export interface SegmentDroppableData {
  type: DROPPABLE_TYPES.SEGMENT;
}

export interface SegmentsTrayDroppableData {
  type: DROPPABLE_TYPES.SEGMENTS_TRAY;
}

export interface OverlaysTrayDroppableData {
  type: DROPPABLE_TYPES.OVERLAYS_TRAY;
  layerIndex: number;
}

export type DroppableData = SegmentDroppableData | SegmentsTrayDroppableData | OverlaysTrayDroppableData;

export interface SegmentDraggableData {
  type: DRAGGABLE_TYPES.SEGMENT;
  kind: SEGMENT_KIND;
}

export interface OverlayDraggableData {
  type: DRAGGABLE_TYPES.OVERLAY;
  kind: OVERLAY_KIND;
  layerIndex: number;
}

export interface OptionDraggableData {
  type: DRAGGABLE_TYPES.OPTION;
  segmentType: SEGMENT_TYPES;
  kind: SEGMENT_KIND | OVERLAY_KIND;
  // Pointer cursor x position relatively to the left edge of the draggable option. This value is used to precisely
  // calculate the position where new segments should be inserted.
  cursorXPosition: Pixels;
}

export type DraggableData = SegmentDraggableData | OverlayDraggableData | OptionDraggableData;

// SEGMENT_TYPES distinct from DRAGGABLE_TYPES by the fact that only SEGMENT_TYPES can be placed inside the timeline.
// Whenever user drops Option on the timeline it turns into SegmentItem or OverlayItem
export type SEGMENT_TYPES = DRAGGABLE_TYPES.SEGMENT | DRAGGABLE_TYPES.OVERLAY;

export interface Segment {
  start: FrameIndex; // start frame index
  end: FrameIndex; // end frame index
}

export type DraggableId = string;

interface DraggableItem {
  draggableId: DraggableId; // unique identifier of the draggable element
  type: DRAGGABLE_TYPES;
  kind: SEGMENT_KIND | OVERLAY_KIND;
  label: string;
  subtitle?: string;
  isLocked: boolean;
}

export interface Source {
  titleId: string;
  inventoryId: string;
  proxyInventoryId?: string; // only segments/overlays with video assigned to them should have `proxyInventoryId` field
  titleName?: string;
  is_screened: boolean | null;
  use_window_start: string | null; // date string in 01/12/2026 format
  use_window_end: string | null; // date string in 01/12/2026 format

  // Some segments can have `start` and `end` fields indicating the range inside the source file that should be played.
  //  These fields don't have relation to the position of segment in the timeline.
  start?: FrameIndex;
  end?: FrameIndex;

  // There should not be `previewUrl` and `previewLoadingError` at the same time. Only one of them.
  previewLoadingError?: { reason: string };
  previewUrl?: string;
  external_id?: string | null;
}

interface SourceBEBase {
  title_id: string;
  inventory_id: string;
  inventory_proxy_id?: string; // The `inventory_proxy_id` exists only in items that are assigned with a video. Overlays with images don't have `inventory_proxy_id`;
}

// Source objects that we receive from the server
export interface SourceBEResponse extends SourceBEBase {
  title_name?: string;
  is_screened: boolean | null;
  use_window_start: string | null; // date string in 01/12/2026 format
  use_window_end: string | null; // date string in 01/12/2026 format
  external_id: string | null;
}

// Source objects that we save to the server.
// The reason we have two different interfaces for BE response and BE request is that because extra fields
// listed in `SourceBEResponse` are derived from the source title and inventory. We don't want the client to update
// those fields.
export type SourceBERequest = SourceBEBase;

interface OverlayBaseProps extends Segment, DraggableItem {
  type: DRAGGABLE_TYPES.OVERLAY;
  kind: OVERLAY_KIND;
}
export type OverlayItemUnassigned = OverlayBaseProps;
export interface OverlayItemWithVideo extends OverlayBaseProps {
  sourceType: Extract<AssetType, 'video'>;
  source: Source;
}
export interface OverlayItemWithImage extends OverlayBaseProps {
  sourceType: Extract<AssetType, 'image'>;
  source: Source;
}

// Represents a draggable Overlay UI element in the timeline
export type OverlayItem = OverlayItemUnassigned | OverlayItemWithVideo | OverlayItemWithImage;
// This mean "extract from `OverlayItem` only those types that have `sourceType` property",
//  since assigned overlays are those that have `sourceType` property
export type OverlayItemAssigned = Extract<OverlayItem, { sourceType: unknown }>;

interface OverlayItemBEBase extends Pick<OverlayItem, 'kind' | 'label'> {
  is_locked: boolean;
  start: number; // for overlays `start` field indicates position in the timeline.
  duration: Frames;
  source_type: Extract<AssetType, 'image' | 'video'> | null;
}

export interface OverlayItemBEResponse extends OverlayItemBEBase {
  source: SourceBEResponse | null;
}

export interface OverlayItemBERequest extends OverlayItemBEBase {
  source: SourceBERequest | null;
}

export type OverlayLayer = OverlayItem[];
export type OverlayLayerBEResponse = OverlayItemBEResponse[];
export type OverlayLayerBERequest = OverlayItemBERequest[];

// Represents draggable Segment UI element in the timeline
export interface SegmentItem extends Segment, DraggableItem {
  type: DRAGGABLE_TYPES.SEGMENT;
  kind: SEGMENT_KIND;
  // When `isIntermediate` is `true` this mean that segment is in the process of being added to the Segments tray.
  // This mean user didn't drop this segment to the timeline yet, but segment was already moved over the tray
  isIntermediate: boolean;
  isSuperLocked: boolean;
  source?: Source;
}

interface SegmentItemBEBase extends Pick<SegmentItem, 'kind' | 'label'> {
  duration: Frames;
  is_locked: boolean;
  is_super_locked: boolean;
  // For segments, `start` is the source frame index from which playback begins.
  // `end` is the source frame index at which playback stops.
  // These fields are unrelated to the segment’s position on the timeline;
  // that position is determined only by the segment’s order and duration.
  start?: number;
  end?: number;
}

export interface SegmentItemBEResponse extends SegmentItemBEBase {
  source: SourceBEResponse | null;
}

export interface SegmentItemBERequest extends SegmentItemBEBase {
  source: SourceBERequest | null;
}

export type SegmentItemAssigned = SegmentItem & {
  source: Source;
};

export type TimelineItemAssigned = OverlayItemAssigned | SegmentItemAssigned;
export type TimelineItem = OverlayItem | SegmentItem;

export interface OverlayOptionItem extends DraggableItem {
  type: DRAGGABLE_TYPES.OPTION;
  kind: OVERLAY_KIND;
  segmentType: DRAGGABLE_TYPES.OVERLAY;
}

export interface SegmentOptionItem extends DraggableItem {
  type: DRAGGABLE_TYPES.OPTION;
  kind: SEGMENT_KIND;
  segmentType: DRAGGABLE_TYPES.SEGMENT;
}

export type OptionItem = OverlayOptionItem | SegmentOptionItem;
